import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { realtimeValidation, validators } from '../../../utils/validationUtils';

const InvestorCriteriaModal = ({ isOpen, onClose, onSaveCriteria }) => {
  const [criteria, setCriteria] = useState({
    investmentType: '',
    investmentBudget: '',
    expectedROI: '',
    investmentHorizon: '',
    preferredLocations: '',
    propertyTypes: [],
    riskTolerance: '',
    financingMethod: '',
    portfolioSize: '',
    investmentGoal: '',
    cashFlow: false,
    appreciation: false,
    taxBenefits: false,
    diversification: false,
    notes: ''
  });
  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (field, value) => {
    setCriteria(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (validationErrors?.[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
    
    // Real-time validation
    const error = realtimeValidation?.validateField(field, value, { ...criteria, [field]: value });
    if (error) {
      setValidationErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleBlur = (field) => {
    const value = criteria?.[field];
    const error = realtimeValidation?.validateOnBlur(field, value, false);
    if (error) {
      setValidationErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleCheckbox = (field) => {
    setCriteria(prev => ({ ...prev, [field]: !prev?.[field] }));
  };

  const handlePropertyTypeToggle = (type) => {
    setCriteria(prev => ({
      ...prev,
      propertyTypes: prev?.propertyTypes?.includes(type)
        ? prev?.propertyTypes?.filter(t => t !== type)
        : [...prev?.propertyTypes, type]
    }));
  };

  const handleSave = () => {
    // Validate investment budget and ROI
    const errors = {};
    
    if (criteria?.investmentBudget && !validators?.isPositiveNumber(criteria?.investmentBudget)) {
      errors.investmentBudget = 'ميزانية الاستثمار يجب أن تكون رقماً موجباً';
    }
    
    if (criteria?.expectedROI && !validators?.isValidPercentage(criteria?.expectedROI)) {
      errors.expectedROI = 'العائد المتوقع يجب أن يكون بين 0 و 100';
    }
    
    if (Object.keys(errors)?.length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    onSaveCriteria?.(criteria);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="معايير المستثمرين" size="lg" footer={null} icon={null} iconColor={null}>
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 text-pink-400">
            <Icon name="TrendingUp" size={20} />
            <span className="font-semibold">تحديد معايير الاستثمار</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">نوع الاستثمار</label>
            <Select
              value={criteria?.investmentType}
              onChange={(e) => handleChange('investmentType', e?.target?.value)}
            >
              <option value="">اختر النوع</option>
              <option value="residential">سكني</option>
              <option value="commercial">تجاري</option>
              <option value="mixed">مختلط</option>
              <option value="land">أراضي</option>
              <option value="off-plan">على الخارطة</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">ميزانية الاستثمار (دينار أردني)</label>
            <Input
              type="number"
              placeholder="مثال: 5000000"
              value={criteria?.investmentBudget}
              onChange={(e) => handleChange('investmentBudget', e?.target?.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">العائد المتوقع (ROI %)</label>
            <Input
              type="number"
              placeholder="مثال: 8"
              value={criteria?.expectedROI}
              onChange={(e) => handleChange('expectedROI', e?.target?.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">أفق الاستثمار</label>
            <Select
              value={criteria?.investmentHorizon}
              onChange={(e) => handleChange('investmentHorizon', e?.target?.value)}
            >
              <option value="">اختر المدة</option>
              <option value="short">قصير الأجل (1-3 سنوات)</option>
              <option value="medium">متوسط الأجل (3-7 سنوات)</option>
              <option value="long">طويل الأجل (7+ سنوات)</option>
            </Select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">المواقع المفضلة</label>
          <Input
            placeholder="مثال: دبي مارينا، وسط دبي، الريم"
            value={criteria?.preferredLocations}
            onChange={(e) => handleChange('preferredLocations', e?.target?.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">أنواع العقارات المفضلة</label>
          <div className="grid grid-cols-2 gap-3">
            {['شقق', 'فلل', 'تاون هاوس', 'بنتهاوس', 'مكاتب', 'محلات']?.map(type => (
              <Checkbox
                key={type}
                label={type}
                checked={criteria?.propertyTypes?.includes(type)}
                onChange={() => handlePropertyTypeToggle(type)}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">تحمل المخاطر</label>
            <Select
              value={criteria?.riskTolerance}
              onChange={(e) => handleChange('riskTolerance', e?.target?.value)}
            >
              <option value="">اختر المستوى</option>
              <option value="conservative">محافظ</option>
              <option value="moderate">معتدل</option>
              <option value="aggressive">جريء</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">طريقة التمويل</label>
            <Select
              value={criteria?.financingMethod}
              onChange={(e) => handleChange('financingMethod', e?.target?.value)}
            >
              <option value="">اختر الطريقة</option>
              <option value="cash">نقدي</option>
              <option value="mortgage">رهن عقاري</option>
              <option value="mixed">مختلط</option>
              <option value="partnership">شراكة</option>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">حجم المحفظة الحالية</label>
            <Select
              value={criteria?.portfolioSize}
              onChange={(e) => handleChange('portfolioSize', e?.target?.value)}
            >
              <option value="">اختر الحجم</option>
              <option value="first-time">مستثمر جديد</option>
              <option value="small">صغيرة (1-3 عقارات)</option>
              <option value="medium">متوسطة (4-10 عقارات)</option>
              <option value="large">كبيرة (10+ عقارات)</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">هدف الاستثمار</label>
            <Select
              value={criteria?.investmentGoal}
              onChange={(e) => handleChange('investmentGoal', e?.target?.value)}
            >
              <option value="">اختر الهدف</option>
              <option value="income">دخل شهري</option>
              <option value="growth">نمو رأس المال</option>
              <option value="balanced">متوازن</option>
              <option value="retirement">تقاعد</option>
            </Select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">أولويات الاستثمار</label>
          <div className="grid grid-cols-2 gap-3">
            <Checkbox
              label="التدفق النقدي"
              checked={criteria?.cashFlow}
              onChange={() => handleCheckbox('cashFlow')}
            />
            <Checkbox
              label="زيادة القيمة"
              checked={criteria?.appreciation}
              onChange={() => handleCheckbox('appreciation')}
            />
            <Checkbox
              label="المزايا الضريبية"
              checked={criteria?.taxBenefits}
              onChange={() => handleCheckbox('taxBenefits')}
            />
            <Checkbox
              label="التنويع"
              checked={criteria?.diversification}
              onChange={() => handleCheckbox('diversification')}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">ملاحظات إضافية</label>
          <textarea
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            rows="3"
            placeholder="أي معايير أو متطلبات إضافية..."
            value={criteria?.notes}
            onChange={(e) => handleChange('notes', e?.target?.value)}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button onClick={handleSave} className="flex-1">
            <Icon name="Save" size={18} className="ml-2" />
            حفظ المعايير
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default InvestorCriteriaModal;